import { ComponentProps, useCallback, useEffect, useRef } from 'react';

import * as analytics from '@adsk/acs-web-analytics';
import {
  useCurrentProductId,
  useCurrentAccountId,
  useCurrentToolId,
  useCurrentProjectId,
} from '@adsk/alloy-provider';
import {
  DropdownMenu,
  OnItemSelect,
  convertGroupKeyToIndex,
} from '@adsk/alloy-react-dropdown';

import { ASSIGNEEPICKER_ANALYTICS } from '../helpers/analytics';
import { ItemWithGroup, GroupedItem, OnIsOpenChange } from '../types';

type InternalItemWithGroup = ItemWithGroup & { fromRecents?: boolean };

const resolveGroupByIndex = (
  index: number | null,
  items: GroupedItem[],
): string => {
  if (index === null || index < 0 || index >= items.length) return 'all';
  // Guard against pinned groups (e.g. 'recents') being set as selectedGroup
  return items[index].pinned ? 'all' : items[index].group;
};

export const useAssigneePickerAnalytics = ({
  groupedItems,
  selectedGroup,
}: {
  groupedItems: GroupedItem[];
  selectedGroup: string;
}) => {
  const currentProjectId = useCurrentProjectId();
  const currentProductId = useCurrentProductId();
  const currentToolId = useCurrentToolId();
  const currentAccountId = useCurrentAccountId();
  const searchText = useRef<string>('');
  const tagsEnabled = useRef<boolean>(true);
  const isMultiple = useRef<boolean>(false);
  const isOpen = useRef<boolean>(false);
  const pendingView = useRef<boolean>(false);

  const trackView = useCallback(() => {
    analytics.track(ASSIGNEEPICKER_ANALYTICS.VIEW, {
      product_id: currentProductId,
      project_id: currentProjectId,
      tool_id: currentToolId,
      account_id: currentAccountId,
      single_selection: !isMultiple.current,
    });
  }, [currentProductId, currentProjectId, currentToolId, currentAccountId]);

  // Emits the view for an open that renderMenu has flagged. Whichever call site
  // below runs first wins; the flag makes the other a no-op, so an open is never
  // reported twice and a StrictMode double-flush collapses to one.
  const flushPendingView = useCallback(() => {
    if (!pendingView.current) return;
    pendingView.current = false;

    trackView();
  }, [trackView]);

  // Covers an open the dropdown never announces: a consumer controlling `isOpen`
  // from its own state gets no onIsOpenChange, and there its own render is what
  // commits the open. No dependency array — it has to see every commit.
  useEffect(flushPendingView);

  // Covers a dropdown-driven open without waiting for a re-render of the component
  // owning this hook, which nothing guarantees.
  const onMenuOpenChange: OnIsOpenChange = useCallback(
    ({ isOpen: isOpenChange }) => {
      if (isOpenChange) flushPendingView();
    },
    [flushPendingView],
  );

  const getSetSelectedGroupKey = useCallback(
    (groupKey: string): string => {
      const group = groupKey
        ? resolveGroupByIndex(convertGroupKeyToIndex(groupKey), groupedItems)
        : 'all';

      analytics.track(ASSIGNEEPICKER_ANALYTICS.GROUP_FILTER_APPLY, {
        product_id: currentProductId,
        project_id: currentProjectId,
        tool_id: currentToolId,
        account_id: currentAccountId,
        primary_filter: group,
        search_chars_num: searchText.current?.length ?? 0,
      });

      return group;
    },
    [
      currentProductId,
      currentProjectId,
      currentToolId,
      currentAccountId,
      groupedItems,
    ],
  );

  const trackItemSelect: OnItemSelect<InternalItemWithGroup> = useCallback(
    (item, { action, selectedItems }) => {
      // The dropdown clears its input on any item click, select or deselect, so
      // a following interaction must not inherit this one's search text.
      const searchCharsNum = searchText.current?.length ?? 0;
      searchText.current = '';

      if (action !== 'select') return;

      analytics.track(ASSIGNEEPICKER_ANALYTICS.ITEM_SELECT, {
        product_id: currentProductId,
        project_id: currentProjectId,
        tool_id: currentToolId,
        account_id: currentAccountId,
        primary_filter: selectedGroup,
        search_chars_num: searchCharsNum,
        selected_members_no: selectedItems.filter((i) => i?.group === 'user')
          .length,
        selected_category: item.group,
        tags_enabled: tagsEnabled.current,
        selected_from_recents: item.fromRecents === true,
      });
    },
    [
      currentProductId,
      currentProjectId,
      currentToolId,
      currentAccountId,
      selectedGroup,
    ],
  );

  // Records the menu state the interaction callbacks above report on. Kept out
  // of the tracking path itself: renderMenu runs on every render, open or not.
  const renderMenu = useCallback(
    (menuProps: ComponentProps<typeof DropdownMenu<ItemWithGroup>>) => {
      const menuIsOpen = !!menuProps.isOpen;

      // Every close path the dropdown drives clears its input without an
      // InputChange, so each open starts from an empty search. Resetting here
      // rather than after the commit keeps it ahead of the InputChange that opens
      // the menu on the first keystroke into a closed picker.
      if (menuIsOpen && !isOpen.current) {
        searchText.current = '';
        pendingView.current = true;
      }

      tagsEnabled.current = !!menuProps.allowGroupFiltering;
      isOpen.current = menuIsOpen;
      isMultiple.current =
        (
          menuProps as ComponentProps<typeof DropdownMenu<ItemWithGroup>> & {
            selectedItems?: ItemWithGroup[];
          }
        ).selectedItems !== undefined;
    },
    [],
  );

  return {
    searchTextRef: searchText,
    getSetSelectedGroupKey,
    renderMenuHandler: renderMenu,
    trackItemSelect,
    onMenuOpenChange,
  };
};
